import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { UnauthorizedPage } from '@/components/auth/unauthorized-page';
import type { PermissionKey } from '@shared/rbac';

/**
 * Map of route path patterns to the permission required to access them.
 * `null` means any authenticated user can access the route.
 */
const ROUTE_PERMISSIONS: Record<string, PermissionKey | null> = {
  '/': null,
  '/customers': 'customers:list',
  '/customers/:id': 'customers:view',
  '/subscriptions': 'subscriptions:list',
  '/invoices': 'invoices:list',
  '/transactions': 'transactions:list',
  '/payment-methods': 'payment_methods:view',
  '/collections': 'collections:list',
  '/collections/:runId': 'collections:view',
  '/dunning': 'dunning:view',
  '/alerts': 'alerts:list',
  '/reports': 'reports:view',
  '/users': 'users:list',
  '/settings': 'settings:view',
};

/**
 * Match the current pathname against the ROUTE_PERMISSIONS map.
 *
 * Static paths are checked first for an exact match. If none is found,
 * parameterised patterns (containing `:`) are tested by converting them
 * to regexes (`:param` → `[^/]+`).
 *
 * Returns the required PermissionKey, `null` (no permission needed), or
 * `undefined` when no matching route pattern exists (treated as "no
 * permission needed" — the router itself handles 404s).
 */
function resolveRoutePermission(pathname: string): PermissionKey | null | undefined {
  // 1. Exact match (covers static routes like '/', '/customers', etc.)
  if (pathname in ROUTE_PERMISSIONS) {
    return ROUTE_PERMISSIONS[pathname];
  }

  // 2. Pattern match (covers parameterised routes like '/customers/:id')
  for (const [pattern, permission] of Object.entries(ROUTE_PERMISSIONS)) {
    if (!pattern.includes(':')) continue;

    const regex = new RegExp(
      `^${pattern.replace(/:[^/]+/g, '[^/]+')}$`,
    );

    if (regex.test(pathname)) {
      return permission;
    }
  }

  // No matching pattern — let the router handle it (catch-all redirect)
  return undefined;
}

/**
 * Auth guard that enforces both authentication and route-level permissions.
 *
 * 1. Unauthenticated users are redirected to `/login`.
 * 2. Authenticated users without the required permission see the
 *    `UnauthorizedPage` component.
 * 3. Authenticated users with the required permission (or routes with
 *    `null` permission) see the child `<Outlet />`.
 */
export function ProtectedRoute() {
  const { isAuthenticated, checkPermission } = useAuth();
  const location = useLocation();

  // --- Authentication check ---
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // --- Permission check ---
  const requiredPermission = resolveRoutePermission(location.pathname);

  // If a permission is required and the user doesn't have it, block access
  if (requiredPermission && !checkPermission(requiredPermission)) {
    return <UnauthorizedPage />;
  }

  return <Outlet />;
}
